"use client";

import { LSingleColumn } from "./LSingleColumn/LSingleColumn";
import { LTwoColsLeft } from "./LTwoColsLeft/LTwoColsLeft";
import { LTwoColsRight } from "./LTwoColsRight/LTwoColsRight";
import { LThreeCols } from "./LThreeCols/LThreeCols";
import { WindowArea } from "../WindowArea/WindowArea";
import {
  getPageLayoutMeta,
  isPageLayoutSymbol,
  type PageLayoutSymbol,
} from "@/lib/layouts/types";
import { useAdmin } from "@/lib/admin/context";

/**
 * HomepageLayoutRenderer — Client Component pickujący konkretny layout
 * component na podstawie symbolu **z `AdminContext`** (live updates po
 * zmianie w panelu admin).
 *
 * **Dlaczego client (od reactive layout switch):** Server Component nie
 * przeładowuje się przy zmianie `currentLayout` w context (musiałby przyjść
 * `revalidatePath`). Wersja client czyta `currentLayout` z `useAdmin()` —
 * zmiana w select dropdownie -> instant re-render z nowym layoutem.
 *
 * **Props vs context:** `symbol` prop = initial fallback (server-side
 * pre-render). Po hydration context wygrywa.
 *
 * Każdy slot wypełniony jest `<WindowArea />` placeholder'em (Faza G).
 * W Fazie F.2+ slots będą wypełniane real content'em z REST API.
 *
 * @param symbol — initial value (z server-side fetch — fallback gdy context
 *                 ma nie-page layout symbol)
 */
export function HomepageLayoutRenderer({ symbol: initialSymbol }: { symbol: PageLayoutSymbol }) {
  const { currentLayout } = useAdmin();
  /* Context wygrywa; fallback do initial gdy currentLayout nie pasuje do
     PageLayoutSymbol (np. ktoś ustawił LCatalog* dla homepage przez DB). */
  const symbol: PageLayoutSymbol = isPageLayoutSymbol(currentLayout) ? currentLayout : initialSymbol;
  const meta = getPageLayoutMeta(symbol);
  const labelFor = (areaSymbol: string) =>
    meta.areas.find((a) => a.symbol === areaSymbol)?.name ?? areaSymbol;

  switch (symbol) {
    case "LSingleColumn":
      return (
        <LSingleColumn
          heroLarge={<WindowArea name="heroLarge" label={labelFor("heroLarge")} />}
          main={<WindowArea name="main" label={labelFor("main")} />}
        />
      );

    case "LTwoColsLeft":
      return (
        <LTwoColsLeft
          leftSidebar={<WindowArea name="leftSidebar" label={labelFor("leftSidebar")} />}
          main={<WindowArea name="main" label={labelFor("main")} />}
        />
      );

    case "LTwoColsRight":
      return (
        <LTwoColsRight
          main={<WindowArea name="main" label={labelFor("main")} />}
          rightSidebar={<WindowArea name="rightSidebar" label={labelFor("rightSidebar")} />}
        />
      );

    case "LThreeCols":
      return (
        <LThreeCols
          leftSidebar={<WindowArea name="leftSidebar" label={labelFor("leftSidebar")} />}
          main={<WindowArea name="main" label={labelFor("main")} />}
          rightSidebar={<WindowArea name="rightSidebar" label={labelFor("rightSidebar")} />}
        />
      );
  }
}
